import { IconButton } from '@atlaskit/button/new';
import SearchIcon from '@atlaskit/icon/core/search';
import { messages } from '@databases/intl/messages';
import {
  type KeyboardShortcut,
  useDebouncedCallback,
  useKeyboardShortcut,
} from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { SearchBar } from '@databases/search-bar/SearchBar';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { isApple } from '../../../utils';

export interface QuickSearchProps {
  value?: DatabaseViewSettings['search'];
  onChange: (value: DatabaseViewSettings['search']) => void;
  tooltip: React.FC<{ children: React.ReactNode }>;
}

export const SearchFieldTransition = styled.div<{ expanded: boolean }>`
  transition: width 0.3s, background 0.3s;

  ${(props) =>
    props.expanded
      ? css`
          width: 382px;
        `
      : css`
          width: 32px;
        `}
`;

const findShortcut: KeyboardShortcut = {
  modifiers: [isApple ? 'metaKey' : 'ctrlKey'],
  code: 'KeyF',
};
const blurShortcut: KeyboardShortcut = { code: 'Escape' };

const QuickSearch: FunctionComponent<QuickSearchProps> = ({
  onChange: onChangeProp,
  value: valueProp,
  tooltip: IconTooltip,
}) => {
  const [value, setLocalValue] = useState(valueProp);
  const debouncedOnChanged = useDebouncedCallback(onChangeProp, 500);
  const isEmpty = !value;
  const isDatabaseContentWrapperEnabled = isFeatureFlagEnabled(
    'databases-frontend-content-wrapper'
  );

  /**
   * The local value should be updated immediately, but the callback
   * to trigger the search should be debounced.
   */
  const updateValue = useCallback(
    (value?: string) => {
      setLocalValue(value);
      debouncedOnChanged(value);
    },
    [setLocalValue, debouncedOnChanged]
  );

  /**
   * Sync local search field state with external changes to current
   * view settings (changing views, etc)
   */
  useEffect(() => {
    setLocalValue(valueProp);
    if (!valueProp) {
      setExpanded(false);
    }
  }, [valueProp]);

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const [expanded, setExpanded] = useState(!isEmpty);

  const { formatMessage } = useIntl();

  const dispatch = defaultStoreUtils.useDispatch();

  const handleExpandField = useCallback(() => {
    dispatch(deselectAll());
    setExpanded(true);
  }, [dispatch]);

  const handleFocus = useCallback(() => {
    searchFieldRef.current?.select();
  }, []);

  useKeyboardShortcut(!expanded ? findShortcut : undefined, handleExpandField);
  useKeyboardShortcut(expanded && !isEmpty ? findShortcut : undefined, handleFocus);

  const handleBlur = useCallback(() => {
    isEmpty && setExpanded(false);
  }, [isEmpty]);

  const handleEscape = useCallback(() => {
    searchFieldRef.current?.blur();
  }, []);

  useKeyboardShortcut(expanded ? blurShortcut : undefined, handleEscape);

  const handleClear = useCallback(() => {
    updateValue(undefined);
    searchFieldRef.current?.focus();
  }, [updateValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!expanded) {
        handleExpandField();
      }

      const value = e.target.value;

      updateValue(value);
    },
    [expanded, handleExpandField, updateValue]
  );

  return (
    <SearchFieldTransition expanded={expanded}>
      {expanded ? (
        <SearchBar
          searchFieldRef={searchFieldRef}
          value={value ?? ''}
          handleOnChange={handleChange}
          handleOnClear={handleClear}
          placeholder={formatMessage(messages['quick-search.search-database-text'])}
          autoComplete="off"
          autoFocus
          onBlur={handleBlur}
        />
      ) : (
        <IconTooltip>
          <IconButton
            icon={(iconProps) => <SearchIcon {...iconProps} size="medium" />}
            label={formatMessage(messages['quick-search.search-database-text'])}
            onClick={handleExpandField}
            appearance={isDatabaseContentWrapperEnabled ? 'subtle' : 'default'}
          />
        </IconTooltip>
      )}
    </SearchFieldTransition>
  );
};

export default QuickSearch;
